import { Vector2 } from 'three';

/**
 * Vignette, scanlines, noise, chromatic aberration, subtle distortion + glitch.
 */
export const MatrixScreenShader = {
  name: 'MatrixScreenShader',
  uniforms: {
    tDiffuse: { value: null },
    uTime: { value: 0 },
    uResolution: { value: new Vector2(1, 1) },
    uScanlines: { value: 1 },
    uNoise: { value: 1 },
    uChromatic: { value: 1 },
    uDistortion: { value: 0 },
    uGlitch: { value: 0 },
    uFlicker: { value: 0 },
    uVignette: { value: 0.55 },
    uBrightness: { value: 1 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform vec2 uResolution;
    uniform float uScanlines;
    uniform float uNoise;
    uniform float uChromatic;
    uniform float uDistortion;
    uniform float uGlitch;
    uniform float uFlicker;
    uniform float uVignette;
    uniform float uBrightness;

    varying vec2 vUv;

    float hash(vec2 p) {
      return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
    }

    void main() {
      vec2 uv = vUv;

      // Distortion only during glitch pulses — no constant “drunk camera” wobble.
      float wave = uGlitch * 0.0045;
      if (wave > 0.00001) {
        uv.x += sin(uv.y * 32.0 + uTime * 6.0) * wave;
        uv.y += cos(uv.y * 28.0 + uTime * 4.5) * wave * 0.5;
        float band = floor(uv.y * 36.0);
        uv.x += sin(band * 13.0 + uTime * 40.0) * uGlitch * 0.002;
      }

      vec2 dir = vUv - 0.5;
      float ca = uChromatic * 0.00055;
      if (uGlitch > 0.05) {
        ca += uGlitch * 0.00035;
      }
      vec3 color;
      color.r = texture2D(tDiffuse, uv + dir * ca).r;
      color.g = texture2D(tDiffuse, uv).g;
      color.b = texture2D(tDiffuse, uv - dir * ca).b;

      color *= uBrightness;
      color += vec3(0.0, uFlicker * 0.035, 0.0) * sin(uTime * 90.0);

      if (uNoise > 0.5) {
        float grain = hash(vUv * uResolution + uTime * 60.0) - 0.5;
        color += grain * 0.022;
      }

      if (uScanlines > 0.5) {
        float scan = sin(vUv.y * uResolution.y * 3.14159265) * 0.5 + 0.5;
        color *= 1.0 - scan * 0.032;
      }

      float vig = 1.0 - dot(dir, dir) * uVignette;
      color *= clamp(vig, 0.82, 1.0);

      gl_FragColor = vec4(color, 1.0);
    }
  `,
};
